import { ProcessEventTriggerExecutionResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerExecutionResponse';
import { ProcessEventTriggerResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerResponse';
import { operationDetailsUrl } from '@app/pages/admin/operation-details.util';
import {
  OrchestrationIdentityLine,
  buildTriggerIdentityLines,
  namedKeyLabel,
  orchestrationEventUrl,
  orchestrationProcessUrl,
  publishedEventIdentifierLabel,
  satelliteProductAnalysisUrlFromEvent,
} from '@app/pages/admin/orchestration-identity.util';
import { assignmentStateSeverity, TagSeverity } from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';

export interface ProcessEventCatalogItem {
  key: string;
  displayName?: string;
  description?: string;
  samplePayloadJson?: string;
}

export interface NamedProcess {
  key: string;
  displayName?: string;
}

export interface EventTriggerView {
  id: string;
  name: string;
  isActive: boolean;
  statusLabel: 'Active' | 'Inactive';
  statusSeverity: TagSeverity;
  sourceProcessKey: string | null;
  sourceProcessLabel: string;
  sourceProcessUrl: string | null;
  targetProcessKey: string;
  targetProcessLabel: string;
  targetProcessUrl: string | null;
  executionCount: number;
  successfulExecutionCount: number;
  failedExecutionCount: number;
  lastTriggeredAt?: string | null;
  lastSourceAssignmentId: string | null;
  lastSourceAssignmentUrl: string | null;
  lastTargetAssignmentId: string | null;
  lastTargetAssignmentUrl: string | null;
  identityLines: OrchestrationIdentityLine[];
}

export interface EventExecutionView {
  id: string;
  eventId: string;
  eventIdUrl: string | null;
  identifierLabel: string;
  triggerId: string;
  triggerName: string;
  state: string;
  stateSeverity: TagSeverity;
  startedAt?: string;
  completedAt?: string | null;
  sourceAssignmentId: string | null;
  sourceAssignmentUrl: string | null;
  targetAssignmentId: string | null;
  targetAssignmentUrl: string | null;
  error: string | null;
}

export interface EventCatalogCard {
  key: string;
  displayName: string;
  description: string;
  samplePayloadJson: string;
  url: string;
  triggerCount: number;
  activeTriggerCount: number;
  lastTriggeredAt?: string | null;
  triggers: EventTriggerView[];
}

export function eventKeysMatch(left?: string | null, right?: string | null): boolean {
  return !!left?.trim() && left.trim() === right?.trim();
}

export function normalizeEventCatalogItem(item: ProcessEventCatalogItem | null | undefined): ProcessEventCatalogItem | null {
  const key = item?.key?.trim() ?? '';
  if (!key) {
    return null;
  }
  return {
    key,
    displayName: item?.displayName?.trim() || key,
    description: item?.description?.trim() ?? '',
    samplePayloadJson: item?.samplePayloadJson ?? '',
  };
}

export function filterTriggersByEventKey(
  items: ProcessEventTriggerResponse[] | null | undefined,
  eventKey: string,
): ProcessEventTriggerResponse[] {
  return (items ?? []).filter(item => eventKeysMatch(item.eventKey, eventKey));
}

export function toEventTriggerView(
  trigger: ProcessEventTriggerResponse,
  eventLabel: string,
  processes: NamedProcess[] | null | undefined,
): EventTriggerView {
  const sourceProcessKey = trigger.sourceProcessDefinitionKey?.trim() || null;
  const targetProcessKey = trigger.targetProcessDefinitionKey?.trim() || '';
  const lastSourceAssignmentId = trigger.lastSourceAssignmentId ?? null;
  const lastTargetAssignmentId = trigger.lastTargetAssignmentId ?? null;
  const isActive = trigger.isActive === true;
  return {
    id: trigger.id ?? '',
    name: trigger.name?.trim() || eventLabel,
    isActive,
    statusLabel: isActive ? 'Active' : 'Inactive',
    statusSeverity: isActive ? 'success' : 'warn',
    sourceProcessKey,
    sourceProcessLabel: sourceProcessKey
      ? namedKeyLabel(processes, sourceProcessKey)
      : 'Any process',
    sourceProcessUrl: orchestrationProcessUrl(sourceProcessKey),
    targetProcessKey,
    targetProcessLabel: namedKeyLabel(processes, targetProcessKey) || targetProcessKey || '—',
    targetProcessUrl: orchestrationProcessUrl(targetProcessKey),
    executionCount: trigger.executionCount ?? 0,
    successfulExecutionCount: trigger.successfulExecutionCount ?? 0,
    failedExecutionCount: trigger.failedExecutionCount ?? 0,
    lastTriggeredAt: trigger.lastTriggeredAt,
    lastSourceAssignmentId,
    lastSourceAssignmentUrl: lastSourceAssignmentId
      ? operationDetailsUrl('Assignment', lastSourceAssignmentId)
      : null,
    lastTargetAssignmentId,
    lastTargetAssignmentUrl: lastTargetAssignmentId
      ? operationDetailsUrl('Assignment', lastTargetAssignmentId)
      : null,
    identityLines: buildTriggerIdentityLines({
      eventKey: trigger.eventKey,
      eventLabel,
      sourceProcessKey,
      sourceProcessLabel: sourceProcessKey ? namedKeyLabel(processes, sourceProcessKey) : undefined,
      targetProcessKey,
      targetProcessLabel: targetProcessKey ? namedKeyLabel(processes, targetProcessKey) : undefined,
    }),
  };
}

export function toEventExecutionView(
  execution: ProcessEventTriggerExecutionResponse,
  trigger: Pick<EventTriggerView, 'id' | 'name'>,
): EventExecutionView {
  const sourceAssignmentId = execution.sourceAssignmentId ?? null;
  const targetAssignmentId = execution.targetAssignmentId ?? null;
  return {
    id: execution.id ?? '',
    eventId: execution.eventId ?? '',
    eventIdUrl: satelliteProductAnalysisUrlFromEvent(execution.eventKey, execution.eventId),
    identifierLabel: publishedEventIdentifierLabel(execution.eventKey),
    triggerId: trigger.id,
    triggerName: trigger.name,
    state: execution.state ?? '—',
    stateSeverity: assignmentStateSeverity(execution.state),
    startedAt: execution.startedAt,
    completedAt: execution.completedAt,
    sourceAssignmentId,
    sourceAssignmentUrl: sourceAssignmentId
      ? operationDetailsUrl('Assignment', sourceAssignmentId)
      : null,
    targetAssignmentId,
    targetAssignmentUrl: targetAssignmentId
      ? operationDetailsUrl('Assignment', targetAssignmentId)
      : null,
    error: execution.error ?? null,
  };
}

export function buildEventCatalog(
  events: ProcessEventCatalogItem[] | null | undefined,
  triggers: ProcessEventTriggerResponse[] | null | undefined,
  processes: NamedProcess[] | null | undefined,
): EventCatalogCard[] {
  const definitions = new Map<string, ProcessEventCatalogItem>();
  for (const item of events ?? []) {
    const normalized = normalizeEventCatalogItem(item);
    if (normalized && !definitions.has(normalized.key)) {
      definitions.set(normalized.key, normalized);
    }
  }
  for (const trigger of triggers ?? []) {
    const key = trigger.eventKey?.trim() ?? '';
    if (key && !definitions.has(key)) {
      definitions.set(key, { key, displayName: key, description: '', samplePayloadJson: '' });
    }
  }

  return [...definitions.values()]
    .map(definition => {
      const related = filterTriggersByEventKey(triggers, definition.key)
        .map(trigger => toEventTriggerView(trigger, definition.displayName || definition.key, processes))
        .sort((left, right) => left.name.localeCompare(right.name));
      const lastTriggeredAt = related
        .map(trigger => trigger.lastTriggeredAt)
        .filter((value): value is string => !!value)
        .sort()
        .at(-1) ?? null;
      return {
        key: definition.key,
        displayName: definition.displayName || definition.key,
        description: definition.description || '',
        samplePayloadJson: definition.samplePayloadJson || '',
        url: orchestrationEventUrl(definition.key) ?? `/admin/orchestration/events/${definition.key}`,
        triggerCount: related.length,
        activeTriggerCount: related.filter(trigger => trigger.isActive).length,
        lastTriggeredAt,
        triggers: related,
      };
    })
    .sort((left, right) => left.displayName.localeCompare(right.displayName) || left.key.localeCompare(right.key));
}

export function findEventCatalogCard(
  cards: EventCatalogCard[] | null | undefined,
  eventKey: string,
): EventCatalogCard | null {
  return (cards ?? []).find(card => eventKeysMatch(card.key, eventKey)) ?? null;
}

export function sortEventExecutions(items: EventExecutionView[]): EventExecutionView[] {
  return [...items].sort((left, right) => {
    const leftAt = left.startedAt ?? '';
    const rightAt = right.startedAt ?? '';
    return rightAt.localeCompare(leftAt);
  });
}
